import { Component, OnInit, ViewChild } from '@angular/core';
import { CommonModule } from '@angular/common';
import { HttpParams } from '@angular/common/http';
import { LoadingAnimationComponent } from '../../shared/components/loading-animation/loading-animation.component';
import { ErrorComponent } from '../../shared/components/orders/error/error.component';
import { ButtonComponent } from '../../shared/components/button/button.component';
import { KeycloakUserManagementFilterComponent } from '../../shared/components/keycloak-user-management/filter/keycloak-user-management-filter.component';
import { KeycloakUserManagementTableComponent } from '../../shared/components/keycloak-user-management/table/keycloak-user-management-table.component';
import { KeycloakUserManagementDialogComponent } from '../../shared/components/keycloak-user-management/dialog/keycloak-user-management-dialog.component';
import {
  KeycloakUserManagementApiError,
  KeycloakUserManagementService,
} from '../../core/keycloak-user-management.service';
import { KeycloakUserManagementResponse } from '../../shared/models/keycloak-user-management/keycloak-user-management-response.model';
import { KeycloakUserDetailResponse } from '../../shared/models/keycloak-user-management/keycloak-user-detail-response.model';
import { AddKeycloakUserPayload } from '../../shared/models/keycloak-user-management/add-keycloak-user-payload.model';
import { UpdateKeycloakUserPayload } from '../../shared/models/keycloak-user-management/update-keycloak-user-payload.model';
import { QueryParamsService } from '../../core/query-params.service';
import { ToastService } from '../../core/toast.service';
import { CustomerFrontendManagementService } from '../../core/customer-frontend-management.service';
import { Observable } from 'rxjs';

type KeycloakUserTab = 'active' | 'inactive';

@Component({
  selector: 'app-keycloak-user-management',
  standalone: true,
  imports: [
    CommonModule,
    LoadingAnimationComponent,
    ErrorComponent,
    ButtonComponent,
    KeycloakUserManagementFilterComponent,
    KeycloakUserManagementTableComponent,
    KeycloakUserManagementDialogComponent,
  ],
  templateUrl: './keycloak-user-management.component.html',
  styleUrl: './keycloak-user-management.component.scss',
})
export class KeycloakUserManagementComponent implements OnInit {
  isLoading = true;
  error: Error | null = null;
  users: KeycloakUserManagementResponse[] = [];
  totalPages = 0;
  currentPage = 0;
  pageSize = 25;
  customerNames = new Map<string, string>();
  activeTab: KeycloakUserTab = 'active';
  readonly tabs: { id: KeycloakUserTab; label: string }[] = [
    { id: 'active', label: 'Aktive Kundennutzer' },
    { id: 'inactive', label: 'Deaktivierte Kundennutzer' },
  ];

  @ViewChild(KeycloakUserManagementDialogComponent) dialog!: KeycloakUserManagementDialogComponent;

  constructor(
    private kumService: KeycloakUserManagementService,
    private queryService: QueryParamsService,
    private toast: ToastService,
    private cfmService: CustomerFrontendManagementService
  ) {}

  ngOnInit(): void {
    const http = this.queryService.getCurrentQueryParams();
    if (!http.get('page') || !http.get('size')) {
      this.queryService.updateQueryParams(
        new HttpParams().set('page', this.currentPage).set('size', this.pageSize)
      );
    }
    this.loadUsers(true);
    this.loadCustomerNames();
  }

  selectTab(tab: KeycloakUserTab): void {
    if (this.activeTab === tab) return;

    this.activeTab = tab;
    const params = this.queryService.getCurrentQueryParams().set('page', 0);
    this.queryService.updateQueryParams(params);
    this.loadUsers(true, params);
  }

  get emptyTableMessage(): string {
    return this.activeTab === 'active'
      ? 'Keine aktiven Kundennutzer vorhanden.'
      : 'Keine deaktivierten Kundennutzer vorhanden.';
  }

  private loadUsers(showLoading = false, params?: HttpParams): void {
    if (showLoading) this.isLoading = true;
    const http = params ?? this.queryService.getCurrentQueryParams();
    this.getUsersForActiveTab(http).subscribe({
      next: (page) => {
        this.error = null;
        this.users = page.content ?? [];
        this.totalPages = page.totalPages ?? 0;
        this.isLoading = false;
      },
      error: (err: Error) => {
        this.error = err;
        this.isLoading = false;
      },
    });
  }

  private loadCustomerNames(): void {
    this.cfmService.getAllCustomers().subscribe({
      next: (customers) => {
        this.customerNames = new Map((customers || []).map((c) => [c.id, c.name]));
      },
    });
  }

  filterUsers(params: HttpParams): void {
    this.isLoading = true;
    this.getUsersForActiveTab(params).subscribe({
      next: (page) => {
        this.error = null;
        this.users = page.content ?? [];
        this.totalPages = page.totalPages ?? 0;
        this.queryService.updateQueryParams(params);
        this.isLoading = false;
      },
      error: (err: Error) => {
        this.error = err;
        this.isLoading = false;
      },
    });
  }

  openAddDialog(): void {
    this.dialog?.showAddDialog();
  }

  openEditDialog(user: KeycloakUserManagementResponse): void {
    this.isLoading = true;
    this.kumService.getKeycloakUserDetails(user.id).subscribe({
      next: (userDetails: KeycloakUserDetailResponse) => {
        this.error = null;
        this.dialog?.showEditDialog(userDetails);
        this.isLoading = false;
      },
      error: (err: Error) => {
        this.error = err;
        this.isLoading = false;
      },
    });
  }

  onAddUser(payload: AddKeycloakUserPayload): void {
    this.kumService.addKeycloakUser(payload).subscribe({
      next: () => {
        this.toast.show(
          'success',
          'Kundennutzer angelegt',
          `Kundennutzer '${payload.email}' wurde erfolgreich erstellt.`
        );
        this.dialog?.close();
        this.activeTab = 'active';
        this.loadUsers(false);
      },
      error: (error: Error) => {
        if (error instanceof KeycloakUserManagementApiError && error.status === 409) {
          this.dialog?.showEmailConflict(this.translateCreateConflictMessage(error.message, payload.email));
        } else {
          this.dialog?.showFormError(this.getMutationErrorMessage(error));
        }
        this.ignoreHandledMutationError();
      },
    });
  }

  onUpdateUser(payload: UpdateKeycloakUserPayload): void {
    this.kumService.updateKeycloakUser(payload).subscribe({
      next: () => {
        this.toast.show(
          'success',
          'Kundennutzer aktualisiert',
          'Der Kundennutzer wurde erfolgreich aktualisiert.'
        );
        this.dialog?.close();
        this.loadUsers(false);
      },
      error: (error: Error) => {
        this.dialog?.showFormError(this.getMutationErrorMessage(error));
        this.ignoreHandledMutationError();
      },
    });
  }

  onRecreateUser(payload: UpdateKeycloakUserPayload): void {
    this.kumService.recreateKeycloakUser(payload).subscribe({
      next: () => {
        this.toast.show(
          'success',
          'Kundennutzer neu gespeichert',
          'Der Kundennutzer wurde erfolgreich erneut in Keycloak angelegt.'
        );
        this.dialog?.close();
        this.loadUsers(false);
      },
      error: (error: Error) => {
        this.dialog?.showFormError(this.getMutationErrorMessage(error));
        this.ignoreHandledMutationError();
      },
    });
  }

  onReactivateUser(id: string): void {
    this.kumService.reactivateKeycloakUser(id).subscribe({
      next: () => {
        this.toast.show('success', 'Kundennutzer aktiviert', 'Der Kundennutzer wurde erfolgreich aktiviert.');
        this.loadUsers(false);
      },
      error: () => this.ignoreHandledMutationError(),
    });
  }

  onDeactivateUser(id: string): void {
    this.kumService.deactivateKeycloakUser(id).subscribe({
      next: () => {
        this.toast.show(
          'success',
          'Kundennutzer deaktiviert',
          'Der Kundennutzer wurde erfolgreich deaktiviert.'
        );
        this.loadUsers(false);
      },
      error: () => this.ignoreHandledMutationError(),
    });
  }

  onDeleteUser(id: string): void {
    this.kumService.deleteKeycloakUser(id).subscribe({
      next: () => {
        this.toast.show(
          'success',
          'Kundennutzer gelöscht',
          'Der deaktivierte Kundennutzer wurde erfolgreich endgültig aus Keycloak gelöscht.'
        );
        this.loadUsers(false);
      },
      error: () => this.ignoreHandledMutationError(),
    });
  }

  onDeleteDatabaseUser(id: string): void {
    this.kumService.deleteDatabaseUser(id).subscribe({
      next: () => {
        this.toast.show(
          'success',
          'Kundennutzer gelöscht',
          'Der Kundennutzer wurde erfolgreich aus der Datenbank gelöscht.'
        );
        this.loadUsers(false);
      },
      error: () => this.ignoreHandledMutationError(),
    });
  }

  private getUsersForActiveTab(params: HttpParams): Observable<{ totalPages: number; content: KeycloakUserManagementResponse[] }> {
    return this.activeTab === 'active'
      ? this.kumService.getKeycloakUsers(params)
      : this.kumService.getInactiveKeycloakUsers(params);
  }

  private ignoreHandledMutationError(): void {
    this.error = null;
  }

  private getMutationErrorMessage(error: Error): string {
    return error instanceof KeycloakUserManagementApiError
      ? error.message
      : 'Der Kundennutzer konnte nicht gespeichert werden.';
  }

  private translateCreateConflictMessage(message: string, email: string): string {
    if (/already exists in keycloak/i.test(message)) {
      return `Ein Kundennutzer mit der E-Mail-Adresse '${email}' existiert bereits.`;
    }

    return message;
  }
}
